import { useEffect, useRef, useState } from 'react'
import * as Tone from 'tone'

import { VideoType } from './useCreateVideo'

export class ToneJsManager {
  ctx: Tone.BaseContext
  streamKeys: Array<string> = []
  delayEffect: Tone.FeedbackDelay
  preFilter: Tone.BiquadFilter
  auxGain: Tone.Gain
  player: Tone.Player | undefined | null
  playerGain: Tone.Gain

  constructor() {
    this.ctx = Tone.getContext()

    //
    this.delayEffect = new Tone.FeedbackDelay('1n', 0.6)
    this.preFilter = new Tone.BiquadFilter(600, 'lowpass')
    this.auxGain = new Tone.Gain(0)

    //
    this.player = new Tone.Player('tokyozombie_128kbps.mp3')
    this.player.loop = true
    this.playerGain = new Tone.Gain(1)
    this.player.chain(this.playerGain, Tone.getDestination())
  }

  randomEffectParam() {
    const dt = Math.min(Math.max(Math.random(), 0.05) + 0.05, 1)
    // this.preFilter.set({ frequency: 300 + Math.random() * 1200 })
    this.preFilter.set({ frequency: 300 + Math.random() * 1200 })
    this.delayEffect.set({
      delayTime: `${dt}s`,
      feedback: Math.max(Math.random() * 0.8, 0.33)
    })
  }

  passThrough() {
    // - NOTE: for a long scene
    this.player.stop()
    this.preFilter.disconnect()
    this.delayEffect.disconnect()
    this.playerGain.gain.rampTo(0, '1s')
    this.auxGain.gain.rampTo(1, '1s')
    this.auxGain.toDestination()
  }

  mix2() {
    // - NOTE: for remix scenes
    this.playerGain.gain.rampTo(0.35, '2s')
    this.auxGain.gain.rampTo(0.65, '2s')
  }

  start() {
    Tone.start()
    this.mute(false)
    this.playerGain.gain.rampTo(1, 0.1)

    if (this.player.loaded) {
      this.player.start()
    } else {
      console.log('lazy playback')
      this.player.autostart = true
    }

    this.auxGain.gain.rampTo(0, 0.1)
  }

  mute(b: boolean) {
    Tone.getDestination().mute = b
  }
}

export type ToneType = {
  instance: ToneJsManager
  attachStream: (stream: VideoType, key: string) => void
  randomFx: () => void
  passThrough: () => void
  mix2: () => void
  start: () => void
  mute: (b: boolean) => void
}

export const useTone = (): ToneType | null => {
  const [tone, setTone] = useState<ToneType | null>(null)
  const isFirst = useRef(true)

  useEffect(() => {
    if (!isFirst.current) {
      return
    }
    isFirst.current = false
    createTone()
  }, [])

  const createTone = () => {
    const instance = new ToneJsManager()

    const toneObject = {
      instance,
      attachStream: (videoStream: VideoType, key: string): void => {
        if (instance.streamKeys.indexOf(key) === -1) {
          const _stream = instance.ctx.createMediaElementSource(videoStream.element)
          const _gainNode = instance.ctx.createGain();
          _gainNode.gain.value = 0.8
          _stream.connect(_gainNode);
          Tone.connect(_gainNode, instance.auxGain)
          instance.auxGain.chain(instance.preFilter, instance.delayEffect, Tone.getDestination())

          instance.streamKeys.push(key)

          console.log('MediaElementSource connect to Tone, ', key)
        }
      },
      start: (): void => { instance.start() },
      randomFx: (): void => { instance.randomEffectParam() },
      passThrough: (): void => { instance.passThrough() },
      mix2: (): void => { instance.mix2() },
      mute: (b: boolean) => { instance.mute(b) }
    }

    setTone(toneObject)
  }

  return tone
}
